import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Chip,
  CircularProgress,
  Alert,
  Breadcrumbs,
  Link,
  Card,
  CardContent,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  Button,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import EditIcon from '@mui/icons-material/Edit';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import BlockIcon from '@mui/icons-material/Block';
import BusinessIcon from '@mui/icons-material/Business';
import PersonIcon from '@mui/icons-material/Person';
import AssessmentIcon from '@mui/icons-material/Assessment';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import HealthAndSafetyIcon from '@mui/icons-material/HealthAndSafety';
import WarningIcon from '@mui/icons-material/Warning';
import RefreshIcon from '@mui/icons-material/Refresh';
import DownloadIcon from '@mui/icons-material/Download';
import api from '../api';
import EditCustomerDialog from '../components/EditCustomerDialog';
import SuspendActivateCustomerDialog from '../components/SuspendActivateCustomerDialog';

interface CustomerDetails {
  tenantID: string;
  companyName: string;
  contactName: string;
  contactEmail: string;
  contactPhone?: string;
  subscriptionType: string;
  enterpriseClientLicense?: string;
  clientLicenseSubscriptionId?: string;
  enterpriseClientLicenseTermUnit?: string;
  enterpriseClientLicenseStartDate?: string;
  enterpriseClientLicenseEndDate?: string;
  enterpriseClientLicenseAutoRenew?: boolean;
  enterpriseClientLicenseExpired?: boolean;
  isActive: boolean;
  createdDate: string;
  lastLoginDate?: string;
  totalDeployments: number;
  activePolicies: number;
}

interface Deployment {
  deploymentID: number;
  deploymentType: string;
  deploymentStatus: string;
  startDate: string;
  endDate?: string;
  licenseScenario: string;
  totalPolicies: number;
  successfulPolicies: number;
  failedPolicies: number;
  skippedPolicies: number;
  executedBy?: string;
}

interface Policy {
  customerPolicyID: number;
  policyID: number;
  policyName: string;
  displayName: string;
  platform: string;
  policyType: string;
  deploymentStatus: string;
  deployedDate?: string;
  lastModifiedDate?: string;
  isCustomized: boolean;
}

interface DiagnosticData {
  overallHealth: string;
  deploymentStats: {
    totalDeployments: number;
    successfulDeployments: number;
    failedDeployments: number;
    successRate: number;
  };
  policyStats: {
    totalPolicies: number;
    deployedPolicies: number;
    failedPolicies: number;
    driftDetected: number;
  };
  recentErrors: Array<{
    errorDate: string;
    errorType: string;
    errorMessage: string;
  }>;
}

export default function CustomerDetails() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [customer, setCustomer] = useState<CustomerDetails | null>(null);
  const [deployments, setDeployments] = useState<Deployment[]>([]);
  const [policies, setPolicies] = useState<Policy[]>([]);
  const [diagnostics, setDiagnostics] = useState<DiagnosticData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [diagnosticsLoading, setDiagnosticsLoading] = useState(false);

  useEffect(() => {
    if (id) {
      fetchCustomerDetails();
    }
  }, [id]);

  const fetchCustomerDetails = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch customer details
      const customerResponse = await api.get(`/TCAdminCustomer/${id}`);
      setCustomer(customerResponse.data);

      // Fetch deployments
      const deploymentsResponse = await api.get(`/TCAdminCustomerDeployments/${id}`);
      setDeployments(deploymentsResponse.data);

      // Fetch policies
      const policiesResponse = await api.get(`/TCAdminCustomerPolicies/${id}`);
      setPolicies(policiesResponse.data);

    } catch (err: any) {
      setError(err.response?.data || 'Failed to load customer details');
      console.error('Error fetching customer details:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatDateTime = (dateString?: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };


  const hasAttachedEnterpriseLicense =
    (customer?.enterpriseClientLicense ?? '').toLowerCase() === 'yes';

  const attachedLicenseTermLabel =
    customer && (customer.enterpriseClientLicenseStartDate || customer.enterpriseClientLicenseEndDate)
      ? `${customer.enterpriseClientLicenseStartDate
          ? formatDate(customer.enterpriseClientLicenseStartDate)
          : 'Unknown start'} - ${customer.enterpriseClientLicenseEndDate
          ? formatDate(customer.enterpriseClientLicenseEndDate)
          : 'No end date'}${customer.enterpriseClientLicenseTermUnit
          ? ` (${customer.enterpriseClientLicenseTermUnit})`
          : ''}`
      : 'N/A';

  const handleEditClick = () => {
    setEditDialogOpen(true);
  };

  const handleEditDialogClose = () => {
    setEditDialogOpen(false);
  };

  const handleEditSave = () => {
    fetchCustomerDetails(); // Refresh customer data
  };

  const handleStatusClick = () => {
    setStatusDialogOpen(true);
  };

  const handleStatusDialogClose = () => {
    setStatusDialogOpen(false);
  };

  const handleStatusSuccess = () => {
    fetchCustomerDetails(); // Refresh customer data
  };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    // Fetch diagnostics when diagnostics tab is selected
    if (newValue === 1 && !diagnostics && id) {
      fetchDiagnostics();
    }
  };

  const fetchDiagnostics = async () => {
    if (!id) return;
    
    try {
      setDiagnosticsLoading(true);
      
      const response = await api.get(`/TCAdminSupport/diagnostics/${id}`);
      setDiagnostics(response.data);
    } catch (err: any) {
      console.error('Error fetching diagnostics:', err);
      setError('Failed to load diagnostics');
    } finally {
      setDiagnosticsLoading(false);
    }
  };

  const handleRunDiagnostics = () => {
    fetchDiagnostics();
  };

  const handleExportDiagnostics = () => {
    if (!diagnostics || !customer) return;
    
    const report = {
      customer: {
        id: customer.tenantID,
        name: customer.companyName,
        subscriptionType: customer.subscriptionType
      },
      timestamp: new Date().toISOString(),
      overallHealth: diagnostics.overallHealth,
      deploymentStats: diagnostics.deploymentStats,
      policyStats: diagnostics.policyStats,
      recentErrors: diagnostics.recentErrors
    };

    const blob = new Blob([JSON.stringify(report, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `diagnostics-${customer.companyName}-${new Date().toISOString().split('T')[0]}.json`;
    link.click();
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !customer) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" onClose={() => setError(null)}>
          {error || 'Customer not found'}
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Breadcrumbs */}
      <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} sx={{ mb: 3 }}>
        <Link
          color="inherit"
          href="#"
          onClick={(e) => {
            e.preventDefault();
            navigate('/customers');
          }}
          sx={{ display: 'flex', alignItems: 'center', textDecoration: 'none' }}
        >
          Customers
        </Link>
        <Typography color="text.primary">{customer.companyName}</Typography>
      </Breadcrumbs>

      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" gutterBottom>
            {customer.companyName}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <Chip
              label={customer.isActive ? 'Active' : 'Suspended'}
              size="small"
              color={customer.isActive ? 'success' : 'error'}
              icon={customer.isActive ? <CheckCircleIcon /> : <BlockIcon />}
              onClick={handleStatusClick}
              sx={{ cursor: 'pointer' }}
            />
            <Tooltip title={customer.isActive ? 'Click to suspend this customer' : 'Click to activate this customer'}>
              <InfoOutlinedIcon sx={{ fontSize: 16, color: 'info.main' }} />
            </Tooltip>
            <Chip
              label={customer.subscriptionType}
              size="small"
              color="primary"
              variant="outlined"
            />
            {hasAttachedEnterpriseLicense && (
              <Chip
                label={customer.enterpriseClientLicenseExpired ? 'Attached License Expired' : 'Attached License Active'}
                size="small"
                color={customer.enterpriseClientLicenseExpired ? 'error' : 'success'}
                variant="outlined"
              />
            )}
          </Box>
        </Box>
        <Button
          variant="contained"
          startIcon={<AssessmentIcon />}
          onClick={() => navigate(`/customers/${id}/gap-assessment`)}
        >
          View Gap Assessment
        </Button>
      </Box>

      <Grid container spacing={3}>
        {/* Customer Information */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <BusinessIcon /> Customer Information
                </Typography>
                <Tooltip title="Edit Customer">
                  <IconButton size="small" color="primary" onClick={handleEditClick}>
                    <EditIcon />
                  </IconButton>
                </Tooltip>
              </Box>
              <Divider sx={{ my: 2 }} />
              
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <PersonIcon color="action" />
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Primary Contact
                    </Typography>
                    <Typography variant="body1">{customer.contactName}</Typography>
                  </Box>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <EmailIcon color="action" />
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Email
                    </Typography>
                    <Typography variant="body1">{customer.contactEmail}</Typography>
                  </Box>
                </Box>

                {customer.contactPhone && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PhoneIcon color="action" />
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Phone
                      </Typography>
                      <Typography variant="body1">{customer.contactPhone}</Typography>
                    </Box>
                  </Box>
                )}

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CalendarTodayIcon color="action" />
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Customer Since
                    </Typography>
                    <Typography variant="body1">{formatDate(customer.createdDate)}</Typography>
                  </Box>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CalendarTodayIcon color="action" />
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Last Login
                    </Typography>
                    <Typography variant="body1">{formatDate(customer.lastLoginDate)}</Typography>
                  </Box>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <WarningIcon color={customer.enterpriseClientLicenseExpired ? 'error' : 'action'} />
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Attached Enterprise License
                    </Typography>
                    <Typography variant="body1">
                      {hasAttachedEnterpriseLicense ? 'Yes' : 'No'}
                    </Typography>
                  </Box>
                </Box>

                {hasAttachedEnterpriseLicense && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CalendarTodayIcon color="action" />
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Attached License Term
                      </Typography>
                      <Typography variant="body1">{attachedLicenseTermLabel}</Typography>
                    </Box>
                  </Box>
                )}

                {hasAttachedEnterpriseLicense && customer.clientLicenseSubscriptionId && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <InfoOutlinedIcon color="action" />
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Attached License Subscription
                      </Typography>
                      <Typography variant="body1">{customer.clientLicenseSubscriptionId}</Typography>
                    </Box>
                  </Box>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Statistics */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Statistics
              </Typography>
              <Divider sx={{ my: 2 }} />
              
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'primary.light', borderRadius: 1 }}>
                    <Typography variant="h3" color="primary.contrastText">
                      {customer.totalDeployments}
                    </Typography>
                    <Typography variant="body2" color="primary.contrastText">
                      Total Deployments
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'success.light', borderRadius: 1 }}>
                    <Typography variant="h3" color="success.contrastText">
                      {customer.activePolicies}
                    </Typography>
                    <Typography variant="body2" color="success.contrastText">
                      Active Policies
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Tabs for Deployments, Policies, and Diagnostics */}
        <Grid item xs={12}>
          <Paper sx={{ width: '100%' }}>
            <Tabs value={tabValue} onChange={handleTabChange} sx={{ borderBottom: 1, borderColor: 'divider' }}>
              <Tab label="Deployments & Policies" />
              <Tab label="Diagnostics" icon={<HealthAndSafetyIcon />} iconPosition="start" />
            </Tabs>

            {/* Tab Panel 0: Deployments & Policies */}
            {tabValue === 0 && (
              <Box sx={{ p: 3 }}>
                {/* Recent Deployments */}
                <Card sx={{ mb: 3 }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Recent Deployments
                    </Typography>
                    <Divider sx={{ my: 2 }} />
              
                    {deployments.length === 0 ? (
                      <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                        No deployments yet
                      </Typography>
                    ) : (
                      <TableContainer>
                        <Table>
                          <TableHead>
                            <TableRow>
                              <TableCell>Type</TableCell>
                              <TableCell>Status</TableCell>
                              <TableCell>Start Date</TableCell>
                              <TableCell>Policies</TableCell>
                              <TableCell align="center">Success Rate</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {deployments.map((deployment) => (
                              <TableRow key={deployment.deploymentID}>
                                <TableCell>{deployment.deploymentType}</TableCell>
                                <TableCell>
                                  <Chip label={deployment.deploymentStatus} size="small" />
                                </TableCell>
                                <TableCell>{formatDateTime(deployment.startDate)}</TableCell>
                                <TableCell>{deployment.totalPolicies}</TableCell>
                                <TableCell align="center">
                                  {deployment.totalPolicies > 0
                                    ? `${Math.round((deployment.successfulPolicies / deployment.totalPolicies) * 100)}%`
                                    : 'N/A'}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    )}
                  </CardContent>
                </Card>

                {/* Active Policies */}
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Active Policies
                    </Typography>
                    <Divider sx={{ my: 2 }} />
                    
                    {policies.length === 0 ? (
                      <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                        No active policies
                      </Typography>
                    ) : (
                      <TableContainer>
                        <Table>
                          <TableHead>
                            <TableRow>
                              <TableCell>Policy Name</TableCell>
                              <TableCell>Platform</TableCell>
                              <TableCell>Type</TableCell>
                              <TableCell>Deployed</TableCell>
                              <TableCell>Status</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {policies.map((policy) => (
                              <TableRow key={policy.customerPolicyID}>
                                <TableCell>
                                  {policy.displayName}
                                  {policy.isCustomized && (
                                    <Chip label="Customized" size="small" sx={{ ml: 1 }} color="info" />
                                  )}
                                </TableCell>
                                <TableCell>
                                  <Chip label={policy.platform} size="small" variant="outlined" />
                                </TableCell>
                                <TableCell>{policy.policyType}</TableCell>
                                <TableCell>{formatDate(policy.deployedDate)}</TableCell>
                                <TableCell>
                                  <Chip
                                    label={policy.deploymentStatus}
                                    size="small"
                                    color={policy.deploymentStatus === 'Deployed' ? 'success' : 'default'}
                                  />
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    )}
                  </CardContent>
                </Card>
              </Box>
            )}

            {/* Tab Panel 1: Diagnostics */}
            {tabValue === 1 && (
              <Box sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                  <Typography variant="h6">Customer Diagnostics</Typography>
                  <Box>
                    <Button
                      startIcon={<RefreshIcon />}
                      onClick={handleRunDiagnostics}
                      disabled={diagnosticsLoading}
                      sx={{ mr: 1 }}
                    >
                      Run Diagnostics
                    </Button>
                    <Button
                      startIcon={<DownloadIcon />}
                      onClick={handleExportDiagnostics}
                      disabled={!diagnostics || diagnosticsLoading}
                      variant="outlined"
                    >
                      Export Report
                    </Button>
                  </Box>
                </Box>

                {diagnosticsLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress />
                  </Box>
                ) : !diagnostics ? (
                  <Alert severity="info">
                    Click "Run Diagnostics" to generate a diagnostic report for this customer.
                  </Alert>
                ) : (
                  <Grid container spacing={3}>
                    {/* Overall Health */}
                    <Grid item xs={12}>
                      <Card>
                        <CardContent>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            {diagnostics.overallHealth === 'Healthy' ? (
                              <CheckCircleIcon sx={{ fontSize: 48, color: 'success.main' }} />
                            ) : (
                              <WarningIcon sx={{ fontSize: 48, color: 'warning.main' }} />
                            )}
                            <Box>
                              <Typography variant="h5">Overall Health</Typography>
                              <Typography
                                variant="h4"
                                color={diagnostics.overallHealth === 'Healthy' ? 'success.main' : 'warning.main'}
                              >
                                {diagnostics.overallHealth}
                              </Typography>
                            </Box>
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>

                    {/* Deployment Statistics */}
                    <Grid item xs={12} md={6}>
                      <Card>
                        <CardContent>
                          <Typography variant="h6" gutterBottom>
                            Deployment Statistics
                          </Typography>
                          <Divider sx={{ my: 2 }} />
                          <Grid container spacing={2}>
                            <Grid item xs={6}>
                              <Box sx={{ textAlign: 'center', p: 2 }}>
                                <Typography variant="h4">{diagnostics.deploymentStats.totalDeployments}</Typography>
                                <Typography variant="body2" color="text.secondary">Total</Typography>
                              </Box>
                            </Grid>
                            <Grid item xs={6}>
                              <Box sx={{ textAlign: 'center', p: 2 }}>
                                <Typography variant="h4" color="success.main">
                                  {diagnostics.deploymentStats.successfulDeployments}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">Successful</Typography>
                              </Box>
                            </Grid>
                            <Grid item xs={6}>
                              <Box sx={{ textAlign: 'center', p: 2 }}>
                                <Typography variant="h4" color="error.main">
                                  {diagnostics.deploymentStats.failedDeployments}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">Failed</Typography>
                              </Box>
                            </Grid>
                            <Grid item xs={6}>
                              <Box sx={{ textAlign: 'center', p: 2 }}>
                                <Typography variant="h4" color="primary.main">
                                  {diagnostics.deploymentStats.successRate.toFixed(1)}%
                                </Typography>
                                <Typography variant="body2" color="text.secondary">Success Rate</Typography>
                              </Box>
                            </Grid>
                          </Grid>
                        </CardContent>
                      </Card>
                    </Grid>

                    {/* Policy Statistics */}
                    <Grid item xs={12} md={6}>
                      <Card>
                        <CardContent>
                          <Typography variant="h6" gutterBottom>
                            Policy Statistics
                          </Typography>
                          <Divider sx={{ my: 2 }} />
                          <Grid container spacing={2}>
                            <Grid item xs={6}>
                              <Box sx={{ textAlign: 'center', p: 2 }}>
                                <Typography variant="h4">{diagnostics.policyStats.totalPolicies}</Typography>
                                <Typography variant="body2" color="text.secondary">Total</Typography>
                              </Box>
                            </Grid>
                            <Grid item xs={6}>
                              <Box sx={{ textAlign: 'center', p: 2 }}>
                                <Typography variant="h4" color="success.main">
                                  {diagnostics.policyStats.deployedPolicies}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">Deployed</Typography>
                              </Box>
                            </Grid>
                            <Grid item xs={6}>
                              <Box sx={{ textAlign: 'center', p: 2 }}>
                                <Typography variant="h4" color="error.main">
                                  {diagnostics.policyStats.failedPolicies}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">Failed</Typography>
                              </Box>
                            </Grid>
                            <Grid item xs={6}>
                              <Box sx={{ textAlign: 'center', p: 2 }}>
                                <Typography variant="h4" color="warning.main">
                                  {diagnostics.policyStats.driftDetected}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">Drift Detected</Typography>
                              </Box>
                            </Grid>
                          </Grid>
                        </CardContent>
                      </Card>
                    </Grid>

                    {/* Recent Errors */}
                    <Grid item xs={12}>
                      <Card>
                        <CardContent>
                          <Typography variant="h6" gutterBottom>
                            Recent Errors (Last 24 Hours)
                          </Typography>
                          <Divider sx={{ my: 2 }} />
                          {diagnostics.recentErrors.length === 0 ? (
                            <Alert severity="success">No errors in the last 24 hours</Alert>
                          ) : (
                            <List>
                              {diagnostics.recentErrors.map((error, index) => (
                                <ListItem key={index} divider={index < diagnostics.recentErrors.length - 1}>
                                  <ListItemText
                                    primary={
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <WarningIcon color="error" fontSize="small" />
                                        <Typography variant="body1">{error.errorType}</Typography>
                                      </Box>
                                    }
                                    secondary={
                                      <>
                                        <Typography variant="body2" component="span">
                                          {error.errorMessage}
                                        </Typography>
                                        <br />
                                        <Typography variant="caption" color="text.secondary">
                                          {formatDateTime(error.errorDate)}
                                        </Typography>
                                      </>
                                    }
                                  />
                                </ListItem>
                              ))}
                            </List>
                          )}
                        </CardContent>
                      </Card>
                    </Grid>
                  </Grid>
                )}
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Dialogs */}
      <EditCustomerDialog
        open={editDialogOpen}
        customer={customer}
        onClose={handleEditDialogClose}
        onSave={handleEditSave}
      />

      <SuspendActivateCustomerDialog
        open={statusDialogOpen}
        customer={customer}
        onClose={handleStatusDialogClose}
        onSuccess={handleStatusSuccess}
      />
    </Box>
  );
}
